import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { motion } from "motion/react";
import { Briefcase, MapPin, Building2 } from "lucide-react";

const jobs = [
  {
    id: "database-architects",
    title: "Database Architects – Multiple Openings",
    location: "Cumming, GA, USA",
    intro:
      "Comsense Technologies LLC is seeking experienced Database Architects to join our team.",
    responsibilities: [
      "Work on KPI reporting and data analysis",
      "Develop and manage ETL processes",
      "Write and optimize SQL queries",
      "Optimize data loading and database performance",
      "Troubleshoot and resolve data-related issues",
    ],
    requirements: [
      "Bachelor’s degree plus 5 years of experience required",
      "Relocation may be required based on client needs",
      "No domestic or international travel required",
    ],
    contact: {
      title: "Director",
      company: "Comsense Technologies LLC",
      addressLines: ["3135 Olde Cornell Ct", "Cumming, GA 30041"],
      emails: ["milind@comsenseconsulting.com", "recruit@comsenseconsulting.com"],
    },
  },
  {
    id: "sfmc-qa",
    title: "Salesforce Marketing Cloud QA Engineer",
    location: "Pune",
    intro:
      "Comsense Technologies is hiring a Salesforce Marketing Cloud QA Engineer with 3+ years of experience.",
    responsibilities: [
      "Perform functional, regression, and end-to-end testing of SFMC solutions",
      "Validate email campaigns, customer journeys, automation workflows, and data extensions",
      "Test Journey Builder, Email Studio, Automation Studio, and Contact Builder functionalities",
      "Perform SQL-based data validation and API testing",
      "Review AMPscript and SSJS logic used in campaigns",
      "Support UAT, defect tracking, and QA processes",
      "Collaborate with developers, analysts, and stakeholders to ensure solution quality",
    ],
    requirements: [
      "Hands-on experience with Salesforce Marketing Cloud testing",
      "Strong knowledge of SQL, AMPscript, and SSJS",
      "Experience with API testing tools such as Postman",
      "Familiarity with QA methodologies, Agile, and defect lifecycle management",
    ],
    meta: [
      { label: "Experience", value: "3+ Years" },
      { label: "Joining", value: "Immediate to 15 Days" },
    ],
    contact: {
      emails: ["shubhamk@comsenseconsulting.com"],
    },
  },
  {
    id: "agentic-ai",
    title: "AI/ML Engineer – Agentic AI Specialist",
    location: "Pune | Work from Office",
    intro:
      "Comsense Technologies is hiring an AI/ML Engineer with 4+ years of experience in building production-grade AI solutions.",
    responsibilities: [
      "Design and develop Agentic AI systems, AI copilots, and intelligent automation workflows",
      "Build and optimize RAG pipelines, semantic search, and conversational AI solutions",
      "Fine-tune LLMs using LoRA, QLoRA, instruction tuning, and RLHF techniques",
      "Develop NLP-to-SQL systems, OCR, and Computer Vision applications",
      "Build REST APIs and backend services using Python and FastAPI",
      "Integrate third-party APIs such as Meta Ads, DV360, and Google Ads",
      "Deploy AI applications on AWS or GCP cloud platforms",
      "Work on predictive modelling, recommendation systems, and analytics projects",
    ],
    requirements: [
      "Python, SQL, FastAPI, LangChain, LangGraph",
      "Pinecone/ChromaDB, Transformers, OpenCV, scikit-learn, XGBoost",
      "Prompt Engineering, LLM Optimization, GCP/AWS",
    ],
    meta: [
      { label: "Experience", value: "4+ Years" },
      { label: "Joining", value: "15 to 30 Days" },
    ],
    contact: {
      emails: ["shubhamk@comsenseconsulting.com"],
    },
  },
];

const Careers = () => {
  return (
    <>
      <Helmet>
        <title>Careers | Comsense Technologies</title>
        <meta
          name="description"
          content="Explore current job openings at Comsense Technologies. Apply for roles across Data, Salesforce Marketing Cloud QA, and AI/ML engineering."
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Careers | Comsense Technologies" />
        <meta
          property="og:description"
          content="Explore current job openings at Comsense Technologies."
        />
        <meta
          property="og:url"
          content="https://comsensetechnologies.com/careers"
        />
        <meta property="og:type" content="website" />
      </Helmet>

      <style>{`
        .careers-page {
          font-family: 'Outfit', sans-serif;
          background: #f8fafc;
          overflow: hidden;
        }

        .careers-hero {
          padding: 70px 0 70px;
          background: #e2f1fe;
          position: relative;
          overflow: hidden;
        }

        .careers-orb-left {
          position: absolute;
          width: 520px;
          height: 520px;
          background: radial-gradient(circle, rgba(6, 123, 177, 0.07) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(140px);
          top: 22%;
          left: -140px;
          z-index: 0;
          animation: careersPulse 5s ease-in-out infinite;
        }

        .careers-orb-right {
          position: absolute;
          width: 420px;
          height: 420px;
          background: radial-gradient(circle, rgba(227, 24, 55, 0.06) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(130px);
          top: -80px;
          right: -120px;
          z-index: 0;
          animation: careersPulse 6s ease-in-out infinite 1.2s;
        }

        @keyframes careersPulse {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); opacity: 0.9; }
          50% { transform: translate3d(0, -8px, 0) scale(1.06); opacity: 1; }
        }

        .careers-hero-inner {
          position: relative;
          z-index: 1;
        }

        .careers-kicker {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 8px 14px;
          border-radius: 999px;
          border: 1px solid rgba(6, 123, 177, 0.22);
          background: rgba(255, 255, 255, 0.7);
          color: #0f172a;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.2px;
        }

        .careers-title {
          margin: 16px 0 10px;
          font-size: 44px;
          font-weight: 800;
          letter-spacing: -0.02em;
          color: #0f172a;
          line-height: 1.08;
        }

        .careers-subtitle {
          margin: 0;
          color: #475569;
          font-size: 16px;
          line-height: 28px;
          max-width: 820px;
        }

        .careers-section {
          padding: 70px 0 90px;
        }

        .job-card {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 22px;
          padding: 26px 24px;
          height: 100%;
          box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
          position: relative;
          overflow: hidden;
        }

        .job-card::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(6, 123, 177, 0.06) 0%, transparent 55%);
          opacity: 0;
          transition: opacity 0.5s ease;
          pointer-events: none;
        }

        .job-card:hover::before {
          opacity: 1;
        }

        .job-title {
          margin: 0 0 10px;
          font-size: 20px;
          font-weight: 800;
          color: #0f172a;
          letter-spacing: -0.01em;
        }

        .job-meta {
          display: flex;
          gap: 14px;
          flex-wrap: wrap;
          align-items: center;
          color: #475569;
          font-size: 14px;
          margin-bottom: 14px;
        }

        .job-meta-item {
          display: inline-flex;
          gap: 8px;
          align-items: center;
          padding: 7px 10px;
          border-radius: 999px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          color: #0f172a;
          font-weight: 600;
        }

        .job-intro {
          margin: 0 0 16px;
          color: #475569;
          font-size: 14px;
          line-height: 26px;
        }

        .job-block-title {
          margin: 18px 0 10px;
          font-size: 14px;
          font-weight: 800;
          color: #0f172a;
          letter-spacing: 0.2px;
          text-transform: uppercase;
        }

        .job-list {
          margin: 0;
          padding-left: 18px;
          color: #475569;
          font-size: 14px;
          line-height: 26px;
        }

        .job-list li + li { margin-top: 6px; }

        .job-contact {
          margin-top: 18px;
          padding-top: 16px;
          border-top: 1px solid #eef2f7;
          color: #475569;
          font-size: 14px;
          line-height: 26px;
        }

        .job-contact a {
          color: #e31837;
          font-weight: 700;
          text-decoration: none;
        }

        .job-actions {
          margin-top: 16px;
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
        }

        .apply-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          border-radius: 12px;
          background: #e31837;
          color: #fff !important;
          text-decoration: none;
          font-weight: 800;
          font-size: 14px;
          box-shadow: 0 12px 26px rgba(227, 24, 55, 0.18);
        }

        .apply-btn:hover {
          color: #fff;
        }

        .contact-chip {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          background: #fff;
          color: #0f172a;
          font-weight: 800;
          text-decoration: none;
          font-size: 14px;
        }

        @media (max-width: 991px) {
          .careers-title { font-size: 36px; }
          .careers-hero { padding: 70px 0 60px; }
        }
      `}</style>

      <div className="careers-page">
        <section className="careers-hero">
          <div className="careers-orb-left" />
          <div className="careers-orb-right" />
          <Container className="careers-hero-inner">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.55 }}
            >
              <div className="careers-kicker">
                <Briefcase size={16} />
                Careers
              </div>
              <h1 className="careers-title">Join Comsense</h1>
              <p className="careers-subtitle">
                Explore our current openings and apply by emailing your resume
                to the contact listed in each posting.
              </p>
            </motion.div>
          </Container>
        </section>

        <section className="careers-section">
          <Container>
            <Row className="g-4">
              {jobs.map((job, idx) => {
                return (
                  <Col key={job.id} lg={6}>
                    <motion.div
                      initial={{ opacity: 1, y: 18 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.25 }}
                      transition={{ duration: 0.5, delay: idx * 0.04 }}
                      whileHover={{ y: -6 }}
                      className="job-card"
                    >
                      <h2 className="job-title">{job.title}</h2>
                      <div className="job-meta">
                        <span className="job-meta-item">
                          <MapPin size={16} />
                          {job.location}
                        </span>
                        {job.meta?.map((item) => (
                          <span key={item.label} className="job-meta-item">
                            <Building2 size={16} />
                            {item.label}: {item.value}
                          </span>
                        ))}
                      </div>

                      <p className="job-intro">{job.intro}</p>

                      <div className="job-block-title">Key Responsibilities</div>
                      <ul className="job-list">
                        {job.responsibilities.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>

                      <div className="job-block-title">Requirements</div>
                      <ul className="job-list">
                        {job.requirements.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>

                      <div className="job-contact">
                        <div style={{ fontWeight: 700, color: "#0f172a" }}>
                          Interested candidates can share their resumes at:
                        </div>
                        {job.contact?.title ? (
                          <div>{job.contact.title}</div>
                        ) : null}
                        {job.contact?.company ? (
                          <div>{job.contact.company}</div>
                        ) : null}
                        {job.contact?.addressLines?.length ? (
                          <div>
                            {job.contact.addressLines.map((line) => (
                              <div key={line}>{line}</div>
                            ))}
                          </div>
                        ) : null}
                        {job.contact?.emails?.length ? (
                          <div>
                            {job.contact.emails.map((email, emailIdx) => (
                              <React.Fragment key={email}>
                                <a href={`mailto:${email}`}>{email}</a>
                                {emailIdx < job.contact.emails.length - 1
                                  ? ", "
                                  : null}
                              </React.Fragment>
                            ))}
                          </div>
                        ) : null}
                        {job.contact?.phone ? (
                          <div>
                            <a href={`tel:${job.contact.phone}`}>
                              {job.contact.phone}
                            </a>
                          </div>
                        ) : null}

                        {/* <div className="job-actions">
                          {mailtoHref ? (
                            <a className="apply-btn" href={mailtoHref}>
                              <Mail size={16} />
                              Apply via Email
                            </a>
                          ) : null}
                          {job.contact?.phone ? (
                            <a
                              className="contact-chip"
                              href={`tel:${job.contact.phone}`}
                            >
                              <Phone size={16} />
                              Call
                            </a>
                          ) : null}
                        </div> */}
                      </div>
                    </motion.div>
                  </Col>
                );
              })}
            </Row>
          </Container>
        </section>
      </div>
    </>
  );
};

export default Careers;
